import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useState } from 'react';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { useAssignedViewModelTags } from '@user/pages/ViewModelPage/hooks';
import { ViewModelTagUnassignConfirmModal } from './ViewModelTagUnassignConfirmModal';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { useTranslation } from 'react-i18next';

type Props = {
  groupId: string;
  viewModelId: string;
  tagId: string;
  tagName: string;
};

const ViewModelTag: React.FC<Props> = ({ groupId, viewModelId, tagId, tagName }: Props) => {
  const { t } = useTranslation();
  const [isUnassignButtonVisible, setUnassignButtonVisible] = useState<boolean>(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  const handleCloseConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
    setUnassignButtonVisible(false);
  }, []);

  return (
    <div
      className="pointer-events-auto relative flex overflow-visible rounded-lg bg-white px-3 py-2 shadow-lg"
      onMouseEnter={() => setUnassignButtonVisible(true)}
      onMouseLeave={() => setUnassignButtonVisible(false)}
    >
      <a
        href={UserPagePathBuilder.tagAssignedViewModelsPage(groupId, tagId)}
        className="font-bold hover:underline"
        target="_blank"
        rel="noreferrer"
      >
        {tagName}
      </a>
      {isUnassignButtonVisible && (
        <div className="absolute right-[-8px] top-[-10px] cursor-pointer" onClick={() => setConfirmModalOpen(true)}>
          <DarkTooltip tooltip={t('viewModel.tag.unassign.tooltip')} placement="right">
            <FontAwesomeIcon icon={faCircleXmark} />
          </DarkTooltip>
        </div>
      )}
      <ViewModelTagUnassignConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        groupId={groupId}
        viewModelId={viewModelId}
        tagId={tagId}
        tagName={tagName}
      />
    </div>
  );
};

type ViewModelTagListProps = {
  viewModel: ViewModelEntity;
  setAddNewTagDisabled: React.Dispatch<React.SetStateAction<boolean>>;
};

const AssignableTagCount = 5;

export const ViewModelTagList: React.FC<ViewModelTagListProps> = ({
  viewModel,
  setAddNewTagDisabled,
}: ViewModelTagListProps) => {
  const { tagList } = useAssignedViewModelTags(viewModel.groupId, viewModel.id);

  useEffect(() => {
    // 設定できる最大数に達した場合、追加ボタンをdisabled
    setAddNewTagDisabled(tagList.length >= AssignableTagCount);
  }, [setAddNewTagDisabled, tagList.length]);

  return (
    <>
      {tagList.map(({ id, name }) => {
        return (
          <ViewModelTag key={id} groupId={viewModel.groupId} viewModelId={viewModel.id} tagId={id} tagName={name} />
        );
      })}
    </>
  );
};
